/**
 * Calendar Timezone Utilities
 * 
 * Minimal utility to convert between UTC (stored) and company timezone (displayed).
 * Used ONLY for timezone interpretation - does NOT change any calendar math or logic.
 * 
 * IMPORTANT: These functions preserve the same hour/minute values that existing
 * calendar calculations expect. They only change WHICH timezone those values
 * are interpreted in.
 */

import { toZonedTime, fromZonedTime, getTimezoneOffset } from 'date-fns-tz';

/**
 * Check if a given local time falls in a DST gap (non-existent time).
 * 
 * During DST spring-forward, clocks jump forward (e.g., 2:00 AM → 3:00 AM),
 * creating a "gap" where certain local times don't exist.
 * 
 * @param {number} year - Full year (e.g., 2026)
 * @param {number} month - Month (0-indexed, e.g., 2 for March)
 * @param {number} day - Day of month
 * @param {number} hours - Hour (0-23)
 * @param {number} minutes - Minutes (0-59)
 * @param {string} timezone - IANA timezone (e.g., "America/New_York")
 * @returns {{ isGap: boolean, gapStart?: string, gapEnd?: string }} - Whether time is in DST gap
 */
export function isDSTGapTime(year, month, day, hours, minutes, timezone) {
  if (!timezone) return { isGap: false };
  
  // Get offset from previous day noon (definitely before any DST transition)
  const prevDayNoon = new Date(Date.UTC(year, month, day - 1, 17, 0, 0, 0));  // noon local = ~17:00 UTC for EST
  const prevDayOffset = getTimezoneOffset(timezone, prevDayNoon);
  
  // Get offset from target day noon (definitely after any DST transition)
  const targetDayNoon = new Date(Date.UTC(year, month, day, 17, 0, 0, 0));
  const targetDayOffset = getTimezoneOffset(timezone, targetDayNoon);
  
  // If offsets are the same, no DST transition on this day
  if (prevDayOffset === targetDayOffset) {
    return { isGap: false };
  }
  
  // DST spring-forward: offset becomes LESS negative (e.g., -5h → -4h)
  // DST fall-back: offset becomes MORE negative (e.g., -4h → -5h)
  const isSpringForward = targetDayOffset > prevDayOffset;
  
  if (!isSpringForward) {
    // Fall-back creates ambiguous times (same local time happens twice), not gaps
    // We don't need to block these - just use one of the mappings
    return { isGap: false };
  }
  
  // For spring-forward, the gap is from the old offset's 2:00 AM to the new offset's 3:00 AM
  // Gap times: hours >= 2 && hours < 3 (assuming standard US DST rules)
  // More precisely: the gap is (offset_diff / 3600000) hours, typically 1 hour
  const gapHours = (targetDayOffset - prevDayOffset) / 3600000;  // Usually 1
  
  // Gap starts at 2:00 AM and ends at (2 + gapHours):00 AM
  // In US: gap is 2:00 - 2:59 (clocks jump from 1:59:59 to 3:00:00)
  const gapStartHour = 2;
  const gapEndHour = gapStartHour + gapHours;
  
  if (hours >= gapStartHour && hours < gapEndHour) {
    return {
      isGap: true,
      gapStart: `${gapStartHour}:00 AM`,
      gapEnd: `${Math.floor(gapEndHour)}:00 AM`
    };
  }
  
  return { isGap: false };
}

/**
 * Parse an ISO string (UTC) and return a Date object that represents
 * the same instant but with getHours()/getMinutes() returning the
 * company timezone values instead of browser timezone values.
 * 
 * This is the KEY function - it makes parseISO results appear in company timezone.
 * 
 * @param {string} isoString - ISO 8601 date string (UTC)
 * @param {string} timezone - IANA timezone (e.g., "America/New_York")
 * @returns {Date} - Date object where local accessors return company timezone values
 */
export function parseISOInCompanyTimezone(isoString, timezone) {
  if (!isoString) return null;
  if (!timezone) {
    // Fallback to browser timezone if no company timezone
    return new Date(isoString);
  }
  
  try {
    // Parse the ISO string to get the UTC instant
    const utcDate = new Date(isoString);
    
    // Convert to zoned time - this shifts the Date so that
    // getHours(), getMinutes(), etc. return values in the target timezone
    return toZonedTime(utcDate, timezone);
  } catch (e) {
    console.warn('[CalendarTZ] Failed to parse in timezone:', isoString, timezone, e);
    return new Date(isoString);
  }
}

/**
 * Get "now" in the company timezone.
 * Returns a Date where getHours(), getDate(), etc. reflect company timezone.
 * 
 * @param {string} timezone - IANA timezone (e.g., "America/New_York")
 * @returns {Date} - Current time in company timezone
 */
export function nowInCompanyTimezone(timezone) {
  if (!timezone) {
    return new Date();
  }
  
  try {
    return toZonedTime(new Date(), timezone);
  } catch (e) {
    console.warn('[CalendarTZ] Failed to get now in timezone:', timezone, e);
    return new Date();
  }
}

/**
 * Convert a Date object (in company timezone representation) back to UTC ISO string.
 * Used when saving drag/resize results.
 * 
 * @param {Date} zonedDate - Date object in company timezone representation
 * @param {string} timezone - IANA timezone (e.g., "America/New_York")
 * @returns {string} - ISO 8601 string in UTC
 */
export function toUTCISO(zonedDate, timezone) {
  if (!zonedDate) return null;
  if (!timezone) {
    return zonedDate.toISOString();
  }
  
  try {
    // Convert from zoned time back to UTC
    const utcDate = fromZonedTime(zonedDate, timezone);
    return utcDate.toISOString();
  } catch (e) {
    console.warn('[CalendarTZ] Failed to convert to UTC:', zonedDate, timezone, e);
    return zonedDate.toISOString();
  }
}

/**
 * Create a Date for a specific date and time in the company timezone,
 * then return the UTC ISO string for API calls.
 * 
 * This is the key function for SAVING - it takes user-selected values
 * (which they see in company timezone) and converts to UTC for storage.
 * 
 * IMPORTANT: This function avoids using new Date(year, month, day, hours, minutes)
 * because JavaScript's Date constructor applies the BROWSER's DST rules, which
 * can corrupt time values during DST transitions. For example, if browser is in
 * PST and we try to create March 8, 2026 2:09 AM, JavaScript adjusts it to 3:09 AM
 * because 2:09 AM doesn't exist in PST either (DST transition). This corrupts
 * the time before we can apply the company timezone's rules.
 * 
 * @param {Date} baseDate - Base date (can be any Date object - we extract year/month/day)
 * @param {string} timeString - Time string in "HH:mm" format (e.g., "18:00")
 * @param {string} timezone - IANA timezone (e.g., "America/New_York")
 * @returns {string} - ISO 8601 string in UTC
 * @throws {Error} - If the selected time is in a DST gap (non-existent time)
 */
export function localTimeToUTCISO(baseDate, timeString, timezone) {
  if (!baseDate || !timeString) return null;
  
  const [hours, minutes] = timeString.split(":").map(Number);
  
  // Extract year/month/day from the base date
  // If baseDate is a zoned representation, use its local values
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const day = baseDate.getDate();
  
  // === DIAGNOSTIC: Log all inputs ===
  console.log('[DIAG-localTimeToUTCISO] INPUT:', {
    'baseDate': baseDate,
    'baseDate.toISOString()': baseDate?.toISOString?.(),
    'extracted': { year, month, day },
    'timeString': timeString,
    'parsed': { hours, minutes },
    timezone
  });
  
  if (!timezone) {
    // No timezone - use browser timezone (legacy behavior)
    const localDate = new Date(year, month, day, hours, minutes, 0, 0);
    return localDate.toISOString();
  }
  
  // Check if this time falls in a DST gap (non-existent time)
  const dstCheck = isDSTGapTime(year, month, day, hours, minutes, timezone);
  console.log('[DIAG-localTimeToUTCISO] DST check:', { dstCheck, year, month, day, hours, minutes });
  
  if (dstCheck.isGap) {
    const error = new Error(
      `This time does not exist on this date because clocks move forward for daylight saving time. ` +
      `On this date, ${dstCheck.gapStart} through ${dstCheck.gapEnd} are skipped. ` +
      `Please choose an earlier time (before ${dstCheck.gapStart}) or later time (${dstCheck.gapEnd} or after).`
    );
    error.isDSTGapError = true;
    error.gapStart = dstCheck.gapStart;
    error.gapEnd = dstCheck.gapEnd;
    throw error;
  }
  
  try {
    // Use Date.UTC to create the time components without browser DST interference.
    const targetAsUtc = Date.UTC(year, month, day, hours, minutes, 0, 0);
    
    // Get timezone offsets to determine which offset to use
    const prevDayNoon = new Date(Date.UTC(year, month, day - 1, 17, 0, 0, 0));
    const targetDayNoon = new Date(Date.UTC(year, month, day, 17, 0, 0, 0));
    
    const prevDayOffset = getTimezoneOffset(timezone, prevDayNoon);
    const targetDayOffset = getTimezoneOffset(timezone, targetDayNoon);
    
    const isDSTTransitionDay = prevDayOffset !== targetDayOffset;
    
    console.log('[DIAG-localTimeToUTCISO] Offset calculation:', {
      prevDayOffset: prevDayOffset / 3600000 + 'h',
      targetDayOffset: targetDayOffset / 3600000 + 'h',
      isDSTTransitionDay
    });
    
    // Determine which offset to use:
    // - For times before the DST gap (hours < 2), use pre-DST offset
    // - For times after the DST gap (hours >= 3 on spring-forward day), use post-DST offset
    // - DST gap times (2:00-2:59) are already rejected above
    let offset;
    if (isDSTTransitionDay && hours < 2) {
      // Before DST transition - use the pre-DST offset
      offset = Math.min(prevDayOffset, targetDayOffset);
      console.log('[DIAG-localTimeToUTCISO] Using pre-DST offset (hours < 2):', offset / 3600000 + 'h');
    } else {
      // After DST transition or non-DST day - use target day offset
      offset = targetDayOffset;
      console.log('[DIAG-localTimeToUTCISO] Using targetDayOffset:', offset / 3600000 + 'h');
    }
    
    // Convert: UTC = localTime - offset
    const utcMs = targetAsUtc - offset;
    const result = new Date(utcMs).toISOString();
    
    console.log('[DIAG-localTimeToUTCISO] OUTPUT:', {
      targetAsUtc: new Date(targetAsUtc).toISOString(),
      offset: offset / 3600000 + 'h',
      'result': result
    });
    
    return result;
  } catch (e) {
    // Re-throw DST gap errors
    if (e.isDSTGapError) throw e;
    
    console.warn('[CalendarTZ] Failed to convert local time to UTC:', baseDate, timeString, timezone, e);
    // Fallback to legacy behavior (may have DST issues in some browsers)
    const localDate = new Date(year, month, day, hours, minutes, 0, 0);
    const utcDate = fromZonedTime(localDate, timezone);
    return utcDate.toISOString();
  }
}

/**
 * Create a Date for a specific date and time in the company timezone.
 * Used when creating new visits.
 * 
 * @param {number} year 
 * @param {number} month - 0-indexed
 * @param {number} day 
 * @param {number} hours 
 * @param {number} minutes 
 * @param {string} timezone - IANA timezone
 * @returns {Date} - Date object in company timezone representation
 */
export function createDateInCompanyTimezone(year, month, day, hours, minutes, timezone) {
  if (!timezone) {
    return new Date(year, month, day, hours, minutes);
  }
  
  try {
    // Create a local date with the specified values
    const localDate = new Date(year, month, day, hours, minutes);
    // This date's local accessors already have the right values,
    // but we need to mark it as being in the company timezone for saves
    // For display purposes, return as-is since local accessors match
    return localDate;
  } catch (e) {
    console.warn('[CalendarTZ] Failed to create date in timezone:', e);
    return new Date(year, month, day, hours, minutes);
  }
}
